// Utilitaires pour formater et normaliser les textes

/**
 * Normalise un texte : première lettre en majuscule, le reste en minuscule
 * @param {string} text - Le texte à normaliser
 * @returns {string} - Le texte normalisé
 */
export const normalizeText = (text) => {
  if (!text || typeof text !== 'string') return ''
  
  const trimmed = text.trim()
  if (trimmed.length === 0) return ''
  
  // Première lettre en majuscule, le reste en minuscule
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

/**
 * Formate une liste en ajoutant des espaces après les virgules
 * @param {Array} items - La liste d'éléments
 * @param {boolean} normalize - Si on doit normaliser chaque élément
 * @returns {string} - La liste formatée
 */
export const formatList = (items, normalize = true) => {
  if (!Array.isArray(items) || items.length === 0) return ''
  
  const processedItems = normalize 
    ? items.map(item => normalizeText(String(item)))
    : items.map(item => String(item))
  
  return processedItems.join(', ')
}

/**
 * Normalise le contenu d'un élément avec timestamp
 * @param {Object} item - L'élément avec content et timestamp
 * @returns {Object} - L'élément avec content normalisé
 */
export const normalizeContentItem = (item) => {
  if (!item || typeof item !== 'object') return item
  
  return {
    ...item,
    content: normalizeText(item.content)
  }
}

/**
 * Normalise un titre d'épisode
 * @param {string} title - Le titre à normaliser
 * @returns {string} - Le titre normalisé
 */
export const normalizeTitle = (title) => {
  if (!title || typeof title !== 'string') return ''
  
  const trimmed = title.trim()
  if (trimmed.length === 0) return ''
  
  // Capitaliser chaque mot important (pas les articles, prépositions courtes)
  const excludeWords = ['de', 'du', 'des', 'le', 'la', 'les', 'un', 'une', 'et', 'ou', 'à', 'au', 'aux']
  
  return trimmed
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // Toujours capitaliser le premier mot
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }
      
      // Ne pas capitaliser les mots exclus sauf s'ils sont après un tiret ou deux points
      if (excludeWords.includes(word)) {
        return word
      }
      
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

/**
 * Formate un résumé en s'assurant qu'il commence par une majuscule et se termine par un point
 * @param {string} summary - Le résumé à formater
 * @returns {string} - Le résumé formaté
 */
export const formatSummary = (summary) => {
  if (!summary || typeof summary !== 'string') return ''
  
  let formatted = summary.trim()
  if (formatted.length === 0) return ''
  
  // Première lettre en majuscule
  formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1)
  
  // Ajouter un point à la fin si pas de ponctuation
  if (!/[.!?]$/.test(formatted)) {
    formatted += '.'
  }
  
  return formatted
}

/**
 * Formate un nom d'invité (garde les majuscules pour les noms propres)
 * @param {string} name - Le nom à formater
 * @returns {string} - Le nom formaté
 */
export const formatGuestName = (name) => {
  if (!name || typeof name !== 'string') return ''
  
  const trimmed = name.trim()
  if (trimmed.length === 0) return ''
  
  // Capitaliser chaque mot pour les noms propres
  return trimmed
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Formate un thème (première lettre en majuscule seulement)
 * @param {string} theme - Le thème à formater
 * @returns {string} - Le thème formaté
 */
export const formatTheme = (theme) => {
  if (!theme || typeof theme !== 'string') return ''
  
  const trimmed = theme.trim().toLowerCase()
  if (trimmed.length === 0) return ''
  
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

/**
 * Formate un timestamp pour l'affichage dans les boutons
 * Affiche le format 00:00:00 si le temps dépasse une heure, sinon 00:00
 * @param {string} timestamp - Le timestamp à formater (ex: "1:23:45" ou "23:45")
 * @returns {string} - Le timestamp formaté
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'string') return ''
  
  const trimmed = timestamp.trim()
  if (trimmed.length === 0) return ''
  
  // Diviser par ':'
  const parts = trimmed.split(':')
  
  if (parts.length === 3) {
    // Format H:MM:SS ou HH:MM:SS - convertir en HH:MM:SS
    const hours = parts[0].padStart(2, '0')
    const minutes = parts[1].padStart(2, '0')
    const seconds = parts[2].padStart(2, '0')
    return `${hours}:${minutes}:${seconds}`
  } else if (parts.length === 2) {
    // Format MM:SS - vérifier si les minutes dépassent 59 (ce qui indiquerait des heures)
    const firstPart = parseInt(parts[0], 10)
    const minutes = parts[1].padStart(2, '0')
    
    if (firstPart >= 60) {
      // Probablement des minutes totales, convertir en heures:minutes:secondes
      const hours = Math.floor(firstPart / 60)
      const remainingMinutes = firstPart % 60
      return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${minutes}`
    } else {
      // Format normal MM:SS, garder tel quel avec padding
      const paddedMinutes = parts[0].padStart(2, '0')
      return `${paddedMinutes}:${minutes}`
    }
  }
  
  // Si le format n'est pas reconnu, retourner tel quel
  return trimmed
}

/**
 * Formate une durée en secondes vers un format lisible
 * @param {number} durationInSeconds - La durée en secondes
 * @returns {string} - La durée formatée (ex: "4 min 9 sec")
 */
export const formatDuration = (durationInSeconds) => {
  if (!durationInSeconds || typeof durationInSeconds !== 'number' || durationInSeconds <= 0) {
    return ''
  }
  
  const minutes = Math.floor(durationInSeconds / 60)
  const seconds = Math.floor(durationInSeconds % 60)
  
  // Format "4 min 9 sec" pour une durée lisible
  if (minutes > 0) {
    return `${minutes} min ${seconds} sec`
  } else {
    return `${seconds} sec`
  }
} 